import { CloseOutlined } from '@ant-design/icons';
import { useFacebookErrorCRUDActions } from '@mm/state/actions/FacebookErrorCRUDActions';
import { activeFacebookPageSelector } from '@mm/state/selectors/facebookPageSelector';
import {
  activeChatbotIdSelector,
  selectActiveCompanyId,
  selectIsShopifyContext,
} from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { FacebookPageErrors } from '../../../../../../common/components/FacebookPageAlerts/FacebookPageErrors';
import { ActivationBlock } from '../ActivationBlock/ActivationBlock';
import { EducationalCardsBlock } from '../EducationalCardsBlock/EducationalCardsBlock';
import css from './AutomationsDashboard.module.css';
import { EmailAnalytics } from '../../Analytics/EmailAnalytics/EmailAnalytics';
import { Modal } from 'antd';
import howtoVideo from 'assets/howto-video.png';
import { useUsersCRUDActions } from '@mm/state/actions/UsersCRUDActions';
import {
  selectCurrentUserId,
  selectSessionSelector,
} from '@mm/state/selectors/userSelector';
import { useResolutionInfo } from '@mm/hooks';
import { InstallationPageForm } from '../../../../../../dashboard/pages/XRayOnboarding/InstallationPage/InstallationPage';
import { selectPromotersByType } from '@mm/state/selectors/promoterSelector';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { PromoterModel, PromoterTypes } from '@mm/state/model/PromoterModel';
import { useConnectKlaviyo } from '../../Integrations/NativeIntegrations/components/KlaviyoIntegrations/use-connect-klaviyo';
import { KlaviyoIcon } from '@mm/icons';
import { StoreState } from '@mm/state/store-interfaces';
import classNames from 'classnames';
import { Button } from '@mm/components';
import { BotBuilderRoutes } from '../../../../../../routes/bot-builder-routes';
import { useHistory } from 'react-router-dom';
import { IntegrationRevenue } from '../../Integrations/NativeIntegrations/components/IntegrationRevenue/IntegrationRevenue';

export const useGetEmailPromoters = () => {
  const activeChatbotId = useSelector(activeChatbotIdSelector);

  const [promotersReady, setPromotersReady] = useState(false);
  const [promoterId, setPromoterId] = useState<number | undefined>();

  const storeGetEmailPromoters = useSelector(state =>
    selectPromotersByType(state, 'getemails'),
  );

  const [promoter, setPromoter] = useState();

  const promoterCRUDActions = usePromoterCRUDActions();

  const retrieveGetemailPromoters = async () => {
    try {
      if (activeChatbotId) {
        const res = await promoterCRUDActions.retrieveAll(
          activeChatbotId,
          'getemails',
        );
        if (res.length) {
          const firstPromoter = res?.[0];
          setPromoterId(firstPromoter.id);
        } else {
          const promoter = await promoterCRUDActions.create({
            botId: activeChatbotId,
            name: 'X-Ray Lead Generation Pixel',
            type: PromoterTypes.GETEMAILS,
            active: false,
          } as PromoterModel);

          setPromoterId(promoter.id);
        }
      }
    } finally {
      setPromotersReady(true);
    }
  };

  useEffect(() => {
    if (storeGetEmailPromoters.length) {
      setPromoterId(storeGetEmailPromoters[0].id);
      setPromotersReady(true);
    } else {
      retrieveGetemailPromoters();
    }
  }, []);

  return {
    promotersReady,
    promoterId,
  };
};

export const NewDashboard = () => {
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const activeFacebookPage = useSelector(activeFacebookPageSelector);
  const emailPromoters = useGetEmailPromoters();

  const session = useSelector(selectSessionSelector);
  const userAttributes = session?.userAttributes;

  const klaviyoRevenue = useSelector(
    (state: StoreState) => state.klaviyo.revenue,
  );
  const hasRevenue = Object.values(klaviyoRevenue).some(v => v > 0);

  const companyId = useSelector(selectActiveCompanyId);
  const currentUserId = useSelector(selectCurrentUserId);

  const { updateUserAttributes } = useUsersCRUDActions();
  const facebookErrorCRUDActions = useFacebookErrorCRUDActions();
  const iframeMouseOverRef = useRef(false);
  const { screenWidth } = useResolutionInfo();
  const { getRevenues } = useConnectKlaviyo();
  const history = useHistory();

  const [showWelcomeSmallModal, setShowWelcomeSmallModal] = useState(false);
  const [showWelcomeModalVideo, setShowWelcomeModalVideo] = useState(false);
  const [startedWatchingVideo, setStartedWatchingVideo] = useState(false);

  const onWindowBlur = () => {
    // Note: this allows us to track if the user clicked the 'play' button in the iframe
    if (iframeMouseOverRef.current) {
      setStartedWatchingVideo(true);
    }
  };

  useEffect(() => {
    if (companyId && currentUserId && startedWatchingVideo) {
      // Note: considering seen if the user has not just opened the modal, but also watched the video
      const timeoutHandle = setTimeout(() => {
        updateUserAttributes(companyId, currentUserId, {
          stopShowingWalkthroughVideo: true,
        });
      }, 2.5 * 60 * 1000); // Note: video is ~5 minutes, but users can watch it a faster speed

      return () => {
        clearTimeout(timeoutHandle);
      };
    }
  }, [companyId, currentUserId, startedWatchingVideo]);

  useEffect(() => {
    window.focus();
    window.addEventListener('blur', onWindowBlur);

    return () => {
      window.removeEventListener('blur', onWindowBlur);
    };
  }, []);

  useEffect(() => {
    if (
      !userAttributes.stopShowingWalkthroughVideo &&
      session?.sessionsCount &&
      session.sessionsCount < 4
    ) {
      setShowWelcomeSmallModal(true);
    }
    if (
      currentUserId &&
      companyId &&
      !userAttributes.stopShowingWalkthroughVideo &&
      session?.sessionsCount &&
      session.sessionsCount > 3
    ) {
      // Note: by agreement with BE team, FE decideds when to set the attribute to stop showing the video.
      updateUserAttributes(companyId, currentUserId, {
        stopShowingWalkthroughVideo: true,
      });
    }
  }, [
    userAttributes.stopShowingWalkthroughVideo,
    companyId,
    currentUserId,
    session?.sessionsCount,
  ]);

  const isShopify = useSelector(selectIsShopifyContext);

  useEffect(() => {
    if (activeFacebookPage?.id) {
      facebookErrorCRUDActions.retrieveAll(activeFacebookPage.id);
    }
  }, [activeFacebookPage?.id]);

  return (
    <div className={css.container}>
      <div className={css.errorsContainer}>
        <FacebookPageErrors />
      </div>

      {/* Note: not using our standard modals approach for this modal not to interfere with other modals,
      as we want to persistently show it in the corner of the page till the user watches it or the user is past first 3 sessions*/}
      {!isShopify && (
        <Modal
          className={css.welcomeModalSmall}
          destroyOnClose
          mask={false}
          style={{
            top: `calc(100vh - ${showWelcomeModalVideo ? '540px' : '360px'})`,
          }}
          open={showWelcomeSmallModal && screenWidth > 700}
          title={
            <div className={css.welcomeModalTitle}>
              Welcome to Customers.ai!
            </div>
          }
          cancelText="Close"
          okText="Watch Short Video"
          closeIcon={<CloseOutlined style={{ color: 'white' }} />}
          cancelButtonProps={{
            style: {
              color: 'white',
              display: showWelcomeModalVideo ? 'block' : 'none',
              borderColor: 'white',
              backgroundColor: '#1a4975',
            },
          }}
          okButtonProps={{
            style: {
              display: showWelcomeModalVideo ? 'none' : 'block',
              color: 'white',
              borderColor: 'white',
              backgroundColor: '#1a4975',
            },
            disabled: showWelcomeModalVideo,
            ghost: true,
          }}
          onOk={() => {
            setShowWelcomeModalVideo(true);
            setStartedWatchingVideo(true);
          }}
          onCancel={() => {
            setShowWelcomeSmallModal(false);
          }}
          width={showWelcomeModalVideo ? 690 : 400}
        >
          {showWelcomeModalVideo ? (
            <div
              onMouseOver={() => {
                iframeMouseOverRef.current = true;
              }}
              onMouseOut={() => {
                window.focus();
                iframeMouseOverRef.current = false;
              }}
            >
              <iframe
                width="640"
                height="394"
                src="https://player.vimeo.com/video/856966839?h=4265e990d9&color=3454EF&title=0&byline=0&autoplay=1"
                allowFullScreen
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture"
                title="X-Ray Pixel Email Capture System in 90 Seconds"
              />
            </div>
          ) : (
            <img
              src={howtoVideo}
              className={css.welcomeModalSmallImage}
              onClick={() => {
                setShowWelcomeModalVideo(true);
                setStartedWatchingVideo(true);
              }}
            />
          )}
        </Modal>
      )}

      <div className={css.channelsContainer}>
        {!activeFacebookPage?.getemailsActivated &&
          emailPromoters.promotersReady && (
            <InstallationPageForm
              botId={activeChatbotId}
              pageRemoteId={activeFacebookPage?.remoteId}
              canSkip={false}
              promoterId={emailPromoters.promoterId}
              css={{
                container: css.installationForm,
                stepsContainer: css.installationStepsContainer,
                step: css.installationStep,
              }}
              canCloseResults={isShopify}
              title="Install X-Ray Pixel to Capture your Leads in 2 Steps"
              subtitle=""
            />
          )}
        {activeFacebookPage?.getemailsActivated && <EducationalCardsBlock />}
        {!isShopify && <ActivationBlock />}
      </div>

      {!isShopify && (
        <div className={css.analytics}>
          {activeFacebookPage && (
            <div
              className={classNames(css.klaviyo, { [css.hidden]: !hasRevenue })}
            >
              <div className={css.klaviyoTitle}>
                <div className={css.klaviyoLogo}>
                  <KlaviyoIcon size={18} color="#262626" />
                  Klaviyo
                </div>
                Revenue generated from your leads
              </div>
              <IntegrationRevenue
                className={css.klaviyoRevenue}
                css={{ revenueBox: css.klaviyoRevenueBox }}
                getRevenues={() =>
                  getRevenues(activeFacebookPage?.id, false, true)
                }
                facebookPageId={activeFacebookPage?.id}
                withSubtext={false}
                platformName="Klaviyo"
                totalRevenue={klaviyoRevenue.totalRevenue}
                totalOrders={klaviyoRevenue.totalOrders}
                totalRevenuePostCreate={klaviyoRevenue.totalRevenuePostCreate}
                totalRevenuePostEmail={klaviyoRevenue.totalRevenuePostEmail}
              />
              <Button
                color="secondary-blue"
                css={{ container: css.secondaryButton }}
                onClick={() => {
                  history.push(
                    BotBuilderRoutes.integrations.getLink({
                      chatbotId: activeChatbotId,
                    }),
                    {
                      kind: 'native-integrations',
                    },
                  );
                }}
              >
                Go to Integration
              </Button>
            </div>
          )}

          <EmailAnalytics
            css={{
              container: classNames(
                css.topContainer,
                css.topContainerWithKlaviyoAnalytics,
              ),
              header: css.topContainerHeader,
              goToAutomationsButton: css.goToAutomationsButton,
            }}
            asAutomationRows
            header="My Automation Analytics"
            perPage={3}
            withPagination={false}
            showEmailDeliverability
          />
        </div>
      )}
    </div>
  );
};
